import * as React from 'react'
import { Text, View, Image } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Button from '@components/button/Button'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import Picker from '@components/picker/Picker'
import SimpleAlert from '@components/modal/SimpleAlert'
import CGU from '@components/cgu/CGU'

import Info from '@screens/account/form/Info'
import Notification from '@screens/account/form/Notification'

import useReducer from '@store/useReducer'
import * as UserStore from '@store/user/user'
import * as AuthStore from '@store/auth/auth'

import useNavigation from '@layout/useNavigation'
import useI18n from '@i18n/useI18n'

import api from '@api/api'

import logger from '@utils/logger'
import { Langage, AuthScope } from '@api/enum'
import { VERSION_NAME } from '@app/config'

interface Props {
  onLogout: () => void
  onDeleteAccount: () => void
}

const FormLayout = (props: Props) => {
  const user = useReducer(UserStore.store, (s) => s.user)

  // screen available only for connected user
  if (!user) {
    return null
  }

  return <Screen {...props} user={user} />
}

interface ScreenProps extends Props {
  user: UserResponse
}

const Screen = ({ onLogout, user, onDeleteAccount }: ScreenProps) => {
  const navigation = useNavigation()
  const i18n = useI18n()

  const updateTime = useReducer(UserStore.store, (s) => s.lastUpdate)
  const preferences = useReducer(UserStore.store, (s) => s.preferences)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const [infoForm, setInfoForm] = React.useState<UserUpdateRequest>({
    prenom: user.prenom,
    nom: user.nom,
    numeroTelephone: user.numeroTelephonePortable,
    fonction: user.fonction,
    langage: user.langage,
  })

  const [notifForm, setNotifForm] = React.useState<PreferenceNotificationRequest[]>(preferences.notifications)
  const [status, setStatus] = React.useState<Status>('loading')
  const [signature, setSignature] = React.useState<FileDetailResponse>()
  const [resettingPassword, setResettingPassword] = React.useState(false)

  React.useEffect(() => {
    Promise.all([api.users.me(), api.notifification.getPreferences()])
      .then(([me, prefs]) => {
        UserStore.actions.setUser(me)
        UserStore.actions.setPreferences({ notifications: prefs })
      })
      .catch(logger.error)
      .finally(() => setStatus('fetched'))
  }, [])

  React.useEffect(() => {
    if (user.signatureFileId) {
      api.files.get(user.signatureFileId).then(setSignature).catch(logger.error)
    }
  }, [user])

  const onInfoForm = (vals: UserUpdateRequest) => {
    setInfoForm(vals)
    return Promise.resolve()
  }

  const onNotifForm = (vals: PreferenceNotificationRequest[]) => {
    setNotifForm(vals)
    return Promise.resolve()
  }

  const errorAlert = () => {
    SimpleAlert.open({
      title: i18n.t('common.errorTitle'),
      description: i18n.t('common.error'),
      buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
    })
  }

  const onSave = () => {
    // save to serveur
    Promise.all([
      api.users.updateUser(infoForm),
      api.notifification.updatePreferences({ preferenceNotificationList: notifForm }),
    ])
      .then(([newUser]) => {
        // save to store
        UserStore.actions.setPreferences({ notifications: notifForm })
        UserStore.actions.setUser(newUser)

        SimpleAlert.open({
          title: i18n.t('common.success'),
          description: i18n.t('screens.account.success'),
          buttons: [
            {
              label: i18n.t('common.ok'),
              onClick: () => {
                SimpleAlert.close()
              },
            },
          ],
        })
      })
      .catch((err) => {
        logger.error(err)
        errorAlert()
      })
  }

  const renderFooter = () => {
    return (
      <Footer>
        <ButtonsContainer>
          <CancelContainer>
            <Cancel onPress={navigation.goBack}>{i18n.t('screens.account.cancel')}</Cancel>
          </CancelContainer>
          <SaveContainer>
            <Button label={i18n.t('screens.account.save')} onClick={onSave} status="active" />
          </SaveContainer>
        </ButtonsContainer>
      </Footer>
    )
  }

  const renderLastModification = () => {
    if (!updateTime) {
      return null
    }

    return (
      <LastModification>{i18n.t('screens.account.lastModification', { date: new Date(updateTime) })}</LastModification>
    )
  }

  const langOptions = i18n.supportedLang.map((key) => ({ value: key, label: i18n.t(`lang.${key}`) }))

  const onLanguageChange = (opt?: PickerOption) => {
    if (opt) {
      const newLang = opt.value === 'fr' ? Langage.FR : Langage.EN
      setInfoForm({ ...infoForm, langage: newLang })

      api.users
        .updateUser({ ...user, langage: newLang })
        .then(UserStore.actions.setUser)
        .catch(logger.error)
    }
  }

  const onResetPassword = () => {
    setResettingPassword(true)
    api.authentification
      .forgottenPassword(user.email)
      .then(() => {
        SimpleAlert.open({
          title: i18n.t('common.success'),
          description: i18n.t('screens.account.identificationSection.resetPasswordSuccess', { email: user.email }),
          buttons: [
            {
              label: i18n.t('common.ok'),
              onClick: () => {
                SimpleAlert.close()
              },
            },
          ],
        })
      })
      .catch((err) => {
        logger.error(err)
        errorAlert()
      })
      .finally(() => setResettingPassword(false))
  }

  return (
    <Root
      loading={status === 'loading'}
      onLeft={navigation.goBack}
      header={i18n.t('screens.account.title')}
      renderFixedBottom={renderFooter}>
      <Header title={i18n.t('screens.account.title')} renderBottom={renderLastModification} />

      <Logout>
        <Button label={i18n.t('common.logout')} onClick={onLogout} status="active" />
      </Logout>

      <MainContainer>
        <Section>
          <SectionTitle title={i18n.t('screens.account.generalSection.title')} />
          <Info form={infoForm} onChange={onInfoForm} user={user} />
        </Section>

        {scopes.includes(AuthScope.UtilisateursInformationModification) && (
          <Section>
            <SectionTitle
              title={i18n.t('screens.account.identificationSection.title')}
              button={{
                label: i18n.t('screens.account.identificationSection.resetPassword'),
                status: resettingPassword ? 'loading' : 'active',
                onClick: () => onResetPassword(),
              }}
            />
          </Section>
        )}

        <Section>
          <SectionTitle title={i18n.t('screens.account.notificationSection.title')} />
          <Notification form={notifForm} onForm={onNotifForm} />
        </Section>

        <Section zIndex={10}>
          <SectionTitle title={i18n.t('screens.account.language')} />
          <PickerContainer>
            <Picker
              options={langOptions}
              onChange={onLanguageChange}
              value={langOptions.find((opt) => opt.value === i18n.lang)}
            />
          </PickerContainer>
        </Section>

        {!!signature && (
          <Section>
            <SectionTitle title={i18n.t('screens.account.signature.title')} />

            <Signature>
              <Picture source={{ uri: signature.url }} size={300} resizeMode="contain" />
            </Signature>
          </Section>
        )}

        <Section>
          <SectionTitle
            title={i18n.t('screens.help.title')}
            button={{ label: i18n.t('screens.help.access'), onClick: () => navigation.push('/account/help') }}
          />
        </Section>

        <Section>
          <SectionTitle title={i18n.t('screens.account.linksSection.title')} />
          <Links>
            <CGU />
            <VersionName>{i18n.t('common.version', { version: VERSION_NAME })}</VersionName>
          </Links>
        </Section>

        <DeleteContainer>
          <Button
            leftIcon="delete"
            label={i18n.t('screens.account.deleteAccount.title')}
            onClick={onDeleteAccount}
            status="active"
            color="cancel"
          />
        </DeleteContainer>
      </MainContainer>
    </Root>
  )
}

export default FormLayout

const MainContainer = styled(View)`
  padding: 24px;
`
const LastModification = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-top: 8px;
`

const Footer = styled(View)`
  justify-content: center;
  flex-direction: row;
  padding: 18px 0;
`

const ButtonsContainer = styled(View)`
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  max-width: 520px;
`

const SaveContainer = styled(View)`
  width: 240px;
`

const CancelContainer = styled(View)`
  width: 240px;
  justify-content: center;
  align-items: center;
`

const Cancel = styled(Text)`
  ${(props) => props.theme.fonts.Callout};
  color: ${(props) => props.theme.colors.primary};
`

const Section = styled(View)<{ zIndex?: number }>`
  margin-bottom: 24px;
  ${(props) => props.zIndex !== undefined && `z-index: ${props.zIndex};`}
`

const Logout = styled(View)`
  position: absolute;
  top: 32px;
  right: 24px;
  z-index: 3;
  width: 200px;
`
const PickerContainer = styled(View)`
  margin: 12px 0;
`

const Signature = styled(View)`
  margin: 16px 0;
  align-self: flex-start;
  border: 1px solid ${(props) => props.theme.colors.disable};
`

const DeleteContainer = styled(View)`
  margin-top: 48px;
  width: 250px;
  align-self: center;
`

const Links = styled(View)`
  margin-top: 24px;
`

const Picture = styled(Image)<{ size: number }>`
  height: ${(props) => props.size}px;
  width: ${(props) => props.size}px;
`

const VersionName = styled(Text)`
  ${(props) => props.theme.fonts.Caption2};
  color: ${(props) => props.theme.colors.secondaryText};
  margin: 16px 0 -16px;
`
